// One lane of the Mail workspace (Session AUB, Items 1, 3, 5 and 8): Reply, Person, Filed or All.
//
// RULE 5: the list is filtered, sorted and paginated ON THE SERVER (GET /api/mail/messages); nothing here re-sorts or
// re-filters a page. The search box is Postgres full text over the subject, the sender and the stored body.
//
// LAPTOP FIRST, PHONE WORKS. On a laptop (1024px and wider) the list and the selected conversation's review card sit side
// by side, the first conversation is already open, and the keyboard drives the review (MAIL_SHORTCUTS). On anything
// narrower, tapping a row opens its card full width with a Back to list control, and the filters fold behind a Filters
// button. Every action a shortcut reaches has a labelled button on the card; the keyboard is only ever an addition.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../../lib/api";
import type { MailAssignee, MailListParams, MailListRow, MailMessageLane, MailSort, MailState } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { fmtLondonDateTime, MAIL_SHORTCUT_HELP, MAIL_STATE_LABELS, shortcutFor } from "../../../lib/mailWorkspace";
import { EmptyState } from "../../../components/EmptyState";
import { StatusPill } from "../../../components/StatusPill";
import { Loader } from "../../../components/Loader";
import { ModalShell, MODAL_ACTIONS, MODAL_SECONDARY_BTN } from "../../../components/ModalShell";
import { useReplyCategories } from "../ReplyTemplatesPage";
import { MailReviewCard, type MailActionDone, type MailReviewCardHandle } from "./MailReviewCard";
import { CustomerPicker, matchText, PHONE_QUERY, senderText, useMediaQuery, WIDE_QUERY } from "./mailUi";

export const MAIL_PAGE_SIZE = 25;

const LABEL = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const FIELD =
  "w-full min-w-0 rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green max-sm:min-h-11";
const SMALL_BTN = "rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong max-sm:min-h-11";

const SORT_LABELS: Record<MailSort, string> = {
  received: "Received",
  subject: "Subject",
  from: "From",
  category: "Kind of email",
  relevance: "Best match",
};

const OUTCOME_LABELS: Record<NonNullable<MailListParams["outcome"]>, string> = {
  pending: "Being decided",
  drafted: "Drafted",
  needs_you: "Needs you",
  no_reply_needed: "No reply needed",
};

const LANE_EMPTY: Record<MailMessageLane, { message: string; hint: string }> = {
  reply: { message: "No replies are waiting for you.", hint: "A conversation appears here when the email engine has a reply ready for you to check and send." },
  person: { message: "Nothing needs a person right now.", hint: "A conversation the engine would not draft (a tender, a complaint, a failed check) appears here." },
  filed: { message: "Nothing has been filed.", hint: "Remittances, supplier invoices and examiner notices the engine filed appear here." },
  all: { message: "No messages recorded yet.", hint: "Every email the mailbox receives is recorded here within minutes." },
};

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function MailLaneView({ lane, assignees, onCountsStale }: { lane: MailMessageLane; assignees: MailAssignee[]; onCountsStale: () => void }) {
  const [params] = useSearchParams();
  const wide = useMediaQuery(WIDE_QUERY, true);
  const phone = useMediaQuery(PHONE_QUERY, false);
  const categories = useReplyCategories();

  // ---- Filters (all sent to the server) -----------------------------------------------------------------------------
  const [q, setQ] = useState("");
  const qDebounced = useDebounced(q.trim(), 300);
  const [customer, setCustomer] = useState<{ id: string; name: string | null } | null>(() => {
    const id = params.get("customerId");
    return id ? { id, name: null } : null;
  });
  const [category, setCategory] = useState("");
  const [outcome, setOutcome] = useState<string>(() => (lane === "all" ? (params.get("outcome") ?? "") : ""));
  const [state, setState] = useState<string>("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [hasAttachment, setHasAttachment] = useState<"" | "true" | "false">("");
  const [assigned, setAssigned] = useState("");
  const [snoozedOnly, setSnoozedOnly] = useState(false);
  const [source, setSource] = useState<"" | "engine" | "legacy">("");
  const [sort, setSort] = useState<MailSort | "">("");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [offset, setOffset] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // ---- The list -----------------------------------------------------------------------------------------------------
  const [rows, setRows] = useState<MailListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const seq = useRef(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<MailReviewCardHandle>(null);

  const effectiveSort: MailSort = sort || (qDebounced ? "relevance" : "received");

  const listParams = useMemo<MailListParams>(() => {
    const p: MailListParams = { lane, sort: effectiveSort, dir, limit: MAIL_PAGE_SIZE, offset };
    if (qDebounced) p.q = qDebounced;
    if (customer) p.customerId = customer.id;
    if (category) p.category = category;
    if (lane === "all" && outcome) p.outcome = outcome as MailListParams["outcome"];
    if (lane === "all" && state) p.state = state as MailState;
    if (from) p.from = from;
    if (to) p.to = to;
    if (hasAttachment) p.hasAttachment = hasAttachment === "true";
    if (assigned) p.assignedTo = assigned;
    if ((lane === "reply" || lane === "person") && snoozedOnly) p.snoozed = "only";
    if ((lane === "reply" || lane === "person") && source) p.source = source;
    return p;
  }, [lane, effectiveSort, dir, offset, qDebounced, customer, category, outcome, state, from, to, hasAttachment, assigned, snoozedOnly, source]);

  const loadList = useCallback(
    async (opts?: { silent?: boolean }) => {
      const mine = ++seq.current;
      if (!opts?.silent) setLoading(true);
      setListError(null);
      try {
        const res = await api.mail.list(listParams);
        if (mine !== seq.current) return;
        setRows(res.messages);
        setTotal(res.total);
      } catch (err) {
        if (mine === seq.current) setListError(friendlyApiError(err, "Could not load the conversations."));
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    },
    [listParams],
  );

  useEffect(() => {
    void loadList();
  }, [loadList]);

  // On a laptop the first conversation is already open, so a review starts by reading, not by clicking.
  useEffect(() => {
    if (!wide) return;
    if (rows.length === 0) return;
    if (!selectedId || !rows.some((r) => r.id === selectedId)) setSelectedId(rows[0]!.id);
  }, [rows, wide, selectedId]);

  const selectedIndex = rows.findIndex((r) => r.id === selectedId);
  const selected = selectedIndex >= 0 ? rows[selectedIndex]! : null;

  function resetPage() {
    setOffset(0);
  }

  function select(row: MailListRow) {
    setSelectedId(row.id);
    setNotice(null);
    if (!wide) setCardOpen(true);
  }

  function move(step: 1 | -1) {
    if (rows.length === 0) return;
    const at = selectedIndex < 0 ? (step === 1 ? 0 : rows.length - 1) : Math.min(rows.length - 1, Math.max(0, selectedIndex + step));
    setSelectedId(rows[at]!.id);
  }

  function skip() {
    if (selectedIndex >= 0 && selectedIndex < rows.length - 1) setSelectedId(rows[selectedIndex + 1]!.id);
  }

  function handleDone(done: MailActionDone) {
    onCountsStale();
    if (done.notice) setNotice(done.notice);
    const moves = done.action === "send" || done.action === "dismiss" || done.action === "snooze";
    if (!moves) {
      void loadList({ silent: true });
      return;
    }
    const leaves = lane === "reply" || lane === "person";
    const idx = selectedIndex;
    const next = idx >= 0 ? (rows[idx + 1] ?? (leaves ? rows[idx - 1] : undefined)) : undefined;
    if (leaves && idx >= 0) {
      const remaining = rows.filter((_, i) => i !== idx);
      setRows(remaining);
      setTotal((t) => Math.max(0, t - 1));
      if (remaining.length === 0) void loadList({ silent: true });
    } else {
      void loadList({ silent: true });
    }
    setSelectedId(next?.id ?? null);
    if (!next) setCardOpen(false);
  }

  // ---- The keyboard (laptop) ----------------------------------------------------------------------------------------
  const keyHandler = useRef<(e: KeyboardEvent) => void>(() => {});
  keyHandler.current = (e: KeyboardEvent) => {
    const dialogOpen = typeof document !== "undefined" && document.querySelector('[role="dialog"]') != null;
    const action = shortcutFor(e, dialogOpen);
    if (!action) return;
    if (action === "help") {
      e.preventDefault();
      setHelpOpen(true);
      return;
    }
    if (action === "search") {
      e.preventDefault();
      searchRef.current?.focus();
      return;
    }
    if (action === "next" || action === "previous") {
      e.preventDefault();
      move(action === "next" ? 1 : -1);
      return;
    }
    if (action === "skip") {
      e.preventDefault();
      skip();
      return;
    }
    if (!selected) return;
    const cardShown = wide || cardOpen;
    if (action === "open") {
      // Enter on a focused button or link keeps its own meaning.
      const t = e.target as HTMLElement | null;
      if (e.key === "Enter" && t && typeof t.closest === "function" && t.closest("button, a")) return;
      e.preventDefault();
      if (!wide) setCardOpen(true);
      else cardRef.current?.focus();
      return;
    }
    if (!cardShown) return;
    const card = cardRef.current;
    if (!card) return;
    e.preventDefault();
    if (action === "send") card.send();
    else if (action === "edit") card.edit();
    else if (action === "assignFirst") card.assignTo(0);
    else if (action === "assignSecond") card.assignTo(1);
    else if (action === "assignMenu") card.openAssign();
    else if (action === "snoozeMenu") card.openSnooze();
    else if (action === "dismiss") card.openDismiss();
    else if (action === "gmail") card.openGmail();
  };
  useEffect(() => {
    const on = (e: KeyboardEvent) => keyHandler.current(e);
    document.addEventListener("keydown", on);
    return () => document.removeEventListener("keydown", on);
  }, []);

  // ---- Rendering ----------------------------------------------------------------------------------------------------
  const replyOrPerson = lane === "reply" || lane === "person";
  const filtersActive =
    Boolean(customer || category || (lane === "all" && (outcome || state)) || from || to || hasAttachment || assigned || (replyOrPerson && (snoozedOnly || source)));

  function clearFilters() {
    setCustomer(null);
    setCategory("");
    setOutcome("");
    setState("");
    setFrom("");
    setTo("");
    setHasAttachment("");
    setAssigned("");
    setSnoozedOnly(false);
    setSource("");
    resetPage();
  }

  const filterFields = (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="mail-filters">
      <div className="min-w-0">
        <span className={LABEL}>Customer</span>
        <CustomerPicker
          label="Filter by customer"
          value={customer?.id ?? null}
          valueName={customer?.name ?? null}
          onChange={(c) => {
            setCustomer(c ? { id: c.id, name: c.name } : null);
            resetPage();
          }}
        />
      </div>
      <label className="min-w-0">
        <span className={LABEL}>Kind of email</span>
        <select value={category} onChange={(e) => { setCategory(e.target.value); resetPage(); }} className={FIELD} aria-label="Filter by kind of email">
          <option value="">Any kind</option>
          {categories.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </select>
      </label>
      {lane === "all" && (
        <>
          <label className="min-w-0">
            <span className={LABEL}>Engine outcome</span>
            <select value={outcome} onChange={(e) => { setOutcome(e.target.value); resetPage(); }} className={FIELD} aria-label="Filter by engine outcome">
              <option value="">Any outcome</option>
              {(Object.keys(OUTCOME_LABELS) as Array<keyof typeof OUTCOME_LABELS>).map((o) => (
                <option key={o} value={o}>
                  {OUTCOME_LABELS[o]}
                </option>
              ))}
            </select>
          </label>
          <label className="min-w-0">
            <span className={LABEL}>Where it stands</span>
            <select value={state} onChange={(e) => { setState(e.target.value); resetPage(); }} className={FIELD} aria-label="Filter by where it stands">
              <option value="">Anywhere</option>
              {(Object.keys(MAIL_STATE_LABELS) as MailState[]).map((s) => (
                <option key={s} value={s}>
                  {MAIL_STATE_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
      <label className="min-w-0">
        <span className={LABEL}>Received from</span>
        <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); resetPage(); }} className={FIELD} aria-label="Received from (date)" />
      </label>
      <label className="min-w-0">
        <span className={LABEL}>Received to</span>
        <input type="date" value={to} onChange={(e) => { setTo(e.target.value); resetPage(); }} className={FIELD} aria-label="Received to (date)" />
      </label>
      <label className="min-w-0">
        <span className={LABEL}>Attachments</span>
        <select value={hasAttachment} onChange={(e) => { setHasAttachment(e.target.value as "" | "true" | "false"); resetPage(); }} className={FIELD} aria-label="Filter by attachments">
          <option value="">With or without</option>
          <option value="true">Has an attachment</option>
          <option value="false">No attachment</option>
        </select>
      </label>
      <label className="min-w-0">
        <span className={LABEL}>Assigned</span>
        <select value={assigned} onChange={(e) => { setAssigned(e.target.value); resetPage(); }} className={FIELD} aria-label="Filter by who it is assigned to">
          <option value="">Anyone or no one</option>
          <option value="me">Assigned to me</option>
          <option value="unassigned">Not assigned</option>
          {assignees.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {a.me ? " (you)" : ""}
            </option>
          ))}
        </select>
      </label>
      {replyOrPerson && (
        <>
          <label className="min-w-0">
            <span className={LABEL}>Where it came from</span>
            <select value={source} onChange={(e) => { setSource(e.target.value as "" | "engine" | "legacy"); resetPage(); }} className={FIELD} aria-label="Filter by where it came from">
              <option value="">Engine and older enquiries</option>
              <option value="engine">The email engine</option>
              <option value="legacy">Older enquiries (before the engine)</option>
            </select>
          </label>
          <label className="flex min-h-11 items-center gap-2 self-end text-sm text-ele-strong">
            <input type="checkbox" checked={snoozedOnly} onChange={(e) => { setSnoozedOnly(e.target.checked); resetPage(); }} />
            Show snoozed only
          </label>
        </>
      )}
    </div>
  );

  const toolbar = (
    <div className="mb-3 space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          ref={searchRef}
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            resetPage();
          }}
          placeholder="Search subject, sender and message text..."
          aria-label="Search mail"
          className={`${FIELD} sm:max-w-md`}
        />
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-ele-body">
            <span>Sort by</span>
            <select
              value={effectiveSort}
              onChange={(e) => {
                setSort(e.target.value as MailSort);
                resetPage();
              }}
              className="rounded-ele border border-ele-field-border bg-ele-field px-2 py-1.5 text-sm text-ele-strong max-sm:min-h-11"
              aria-label="Sort by"
            >
              {(Object.keys(SORT_LABELS) as MailSort[])
                .filter((s) => s !== "relevance" || qDebounced)
                .map((s) => (
                  <option key={s} value={s}>
                    {SORT_LABELS[s]}
                  </option>
                ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => {
              setDir((d) => (d === "desc" ? "asc" : "desc"));
              resetPage();
            }}
            className={SMALL_BTN}
            aria-label={`Sort order: ${dir === "desc" ? "descending" : "ascending"}. Change it`}
          >
            {effectiveSort === "received" ? (dir === "desc" ? "Newest first" : "Oldest first") : dir === "desc" ? "Z to A / best first" : "A to Z"}
          </button>
          {!wide && (
            <button type="button" onClick={() => setFiltersOpen((o) => !o)} aria-expanded={filtersOpen} className={SMALL_BTN}>
              {filtersOpen ? "Hide filters" : filtersActive ? "Filters (on)" : "Filters"}
            </button>
          )}
          {filtersActive && (
            <button type="button" onClick={clearFilters} className={SMALL_BTN}>
              Clear filters
            </button>
          )}
          {wide && (
            <button type="button" onClick={() => setHelpOpen(true)} className="font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong">
              Keyboard shortcuts (?)
            </button>
          )}
        </div>
      </div>
      {(wide || filtersOpen) && filterFields}
    </div>
  );

  const pager = (
    <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-ele-body" data-testid="mail-pager">
      <span>
        {total === 0 ? "No conversations" : `Showing ${offset + 1} to ${offset + rows.length} of ${total}`}
      </span>
      <div className="flex gap-2">
        <button type="button" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - MAIL_PAGE_SIZE))} className={`${SMALL_BTN} disabled:opacity-40`}>
          Previous
        </button>
        <button type="button" disabled={offset + MAIL_PAGE_SIZE >= total || loading} onClick={() => setOffset(offset + MAIL_PAGE_SIZE)} className={`${SMALL_BTN} disabled:opacity-40`}>
          Next
        </button>
      </div>
    </div>
  );

  let list: ReactNode;
  if (loading && rows.length === 0) {
    list = (
      <Loader label="Loading the conversations..." rows={6} />
    );
  } else if (listError) {
    list = (
      <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
        <p className="text-ele-strong">{listError}</p>
        <button type="button" onClick={() => void loadList()} className="mt-3 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong max-sm:min-h-11">
          Retry
        </button>
      </div>
    );
  } else if (rows.length === 0) {
    const searching = qDebounced || filtersActive;
    list = (
      <EmptyState
        message={searching ? (qDebounced ? `Nothing matches "${qDebounced}".` : "Nothing matches these filters.") : LANE_EMPTY[lane].message}
        hint={searching ? undefined : LANE_EMPTY[lane].hint}
      />
    );
  } else if (phone) {
    list = (
      <>
        <ul className="space-y-2" aria-label="Conversations">
          {rows.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => select(r)}
                aria-current={r.id === selectedId ? "true" : undefined}
                className={`block w-full min-w-0 rounded-ele border px-3 py-2 text-left ${r.id === selectedId ? "border-ele-green bg-ele-inset" : "border-ele-purple-accent bg-ele-surface"}`}
                data-testid={`mail-row-${r.id}`}
              >
                <span className="block truncate font-heading text-ele-strong">{r.subject?.trim() || "(no subject)"}</span>
                <span className="mt-0.5 block truncate text-xs text-ele-body">{senderText(r)}</span>
                <span className="mt-0.5 block text-xs text-ele-body">
                  {fmtLondonDateTime(r.receivedAt)} · {matchText(r.match).text}
                </span>
                <RowBadges row={r} lane={lane} />
              </button>
            </li>
          ))}
        </ul>
        {pager}
      </>
    );
  } else {
    list = (
      <>
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "58%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <th className="px-3 py-2">Conversation</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Received</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const m = matchText(r.match);
                const on = r.id === selectedId;
                return (
                  <tr
                    key={r.id}
                    aria-selected={on}
                    data-testid={`mail-row-${r.id}`}
                    onClick={() => select(r)}
                    className={`cursor-pointer border-t border-ele-purple-accent transition ${on ? "bg-ele-inset" : "hover:bg-ele-inset"}`}
                  >
                    <td className="px-3 py-2 align-top">
                      <button type="button" onClick={(e) => { e.stopPropagation(); select(r); }} className="block w-full truncate text-left font-heading text-ele-strong">
                        {r.subject?.trim() || "(no subject)"}
                      </button>
                      <div className="truncate text-xs text-ele-body" title={senderText(r)}>
                        {senderText(r)}
                      </div>
                      <RowBadges row={r} lane={lane} />
                    </td>
                    <td className={`truncate px-3 py-2 align-top ${m.tone === "strong" ? "text-ele-strong" : "text-ele-body"}`} title={m.text}>
                      {m.text}
                    </td>
                    <td className="truncate px-3 py-2 align-top text-ele-body">{fmtLondonDateTime(r.receivedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pager}
      </>
    );
  }

  const card = selected ? (
    <MailReviewCard
      key={selected.id}
      ref={cardRef}
      row={selected}
      assignees={assignees}
      wide={wide}
      onBack={wide ? undefined : () => setCardOpen(false)}
      onSkip={skip}
      onDone={handleDone}
    />
  ) : null;

  const noticeEl = notice ? (
    <p role="status" className="mb-3 rounded-ele border border-ele-green bg-ele-field-inset px-3 py-2 text-sm text-ele-strong">
      {notice}
    </p>
  ) : null;

  const helpModal = helpOpen ? (
    <ModalShell ariaLabel="Keyboard shortcuts" onClose={() => setHelpOpen(false)} closeOnBackdrop width="max-w-md" panelClassName="p-5">
      <h2 className="font-heading text-lg text-ele-strong">Keyboard shortcuts</h2>
      <p className="mt-1 text-sm text-ele-body">They work on a laptop while you are not typing in a box. Every one has a button too.</p>
      <dl className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
        {MAIL_SHORTCUT_HELP.map((h) => (
          <div key={h.keys} className="contents">
            <dt className="font-menu text-ele-strong">{h.keys}</dt>
            <dd className="text-ele-body">{h.does}</dd>
          </div>
        ))}
      </dl>
      <div className={`mt-4 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={() => setHelpOpen(false)} className={MODAL_SECONDARY_BTN}>
          Close
        </button>
      </div>
    </ModalShell>
  ) : null;

  if (!wide && cardOpen && card) {
    return (
      <div className="min-w-0">
        {noticeEl}
        {card}
        {helpModal}
      </div>
    );
  }

  if (!wide) {
    return (
      <div className="min-w-0">
        {noticeEl}
        {toolbar}
        {list}
        {helpModal}
      </div>
    );
  }

  return (
    <div className="min-w-0">
      {noticeEl}
      {toolbar}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
        <div className="min-w-0">{list}</div>
        <div className="min-w-0">
          {card ?? (
            <div className="rounded-ele border border-ele-hairline bg-ele-surface p-6 text-center text-sm text-ele-body">
              {rows.length > 0 ? "Choose a conversation to read it and its reply." : "Nothing to review here."}
            </div>
          )}
        </div>
      </div>
      {helpModal}
    </div>
  );
}

function RowBadges({ row, lane }: { row: MailListRow; lane: MailMessageLane }) {
  const badges: Array<{ text: string; tone: "green" | "amber" | "grey" | "purple" | "blue"; testId?: string }> = [];
  if (row.draftLabel) badges.push({ text: row.draftLabel, tone: row.freeDraft ? "amber" : "green", testId: `draft-label-${row.id}` });
  if (row.replyOverdue) badges.push({ text: "Reply overdue (24h target)", tone: "amber" });
  // Session AUE (D-LATE-1): the reply would go out late, so the platform adds its one fixed apology after the greeting.
  if (row.lateReply) badges.push({ text: row.lateReply, tone: "amber", testId: `late-reply-${row.id}` });
  if (row.assigned) badges.push({ text: `Assigned: ${row.assigned.name ?? "someone"}`, tone: "purple" });
  if (row.snoozedUntil) badges.push({ text: `Snoozed until ${fmtLondonDateTime(row.snoozedUntil)}`, tone: "grey" });
  if (row.hasAttachments) badges.push({ text: "Attachment", tone: "grey" });
  if (row.beforeEngine) badges.push({ text: "Before the email engine", tone: "grey" });
  if (lane === "all") badges.push({ text: row.stateLabel, tone: "blue" });
  if (lane === "filed" && row.fileKindLabel) badges.push({ text: row.fileKindLabel, tone: "grey" });
  // 5 October 2026: a message the engine decided needs no reply says why on its row, in the engine's own words, so the office
  // can see at a glance that nothing was missed (and which rule closed it).
  const reason = row.outcome === "no_reply_needed" && row.outcomeReason?.trim() ? row.outcomeReason.trim() : null;
  if (badges.length === 0 && !reason) return null;
  return (
    <>
      {badges.length > 0 && (
        <span className="mt-1 flex flex-wrap gap-1">
          {badges.map((b) => (
            <span key={b.text} data-testid={b.testId} className="inline-flex max-w-full">
              <StatusPill word={b.text} tone={b.tone} className="max-w-full truncate" />
            </span>
          ))}
        </span>
      )}
      {reason && (
        <span className="mt-1 block truncate text-xs text-ele-body" title={reason} data-testid={`engine-reason-${row.id}`}>
          {reason}
        </span>
      )}
    </>
  );
}
